import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FirstAidService } from '../../../../services/first-aid/first-aid.service';
import { FirstAidForm } from '../../../../services/first-aid/first-aid.interface';
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-first-aid-form',
  imports: [
    CommonModule,
    MatCardModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatProgressBar,
    MatFormFieldModule,
    MatCardModule,
    MatInputModule,
    MatExpansionModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './first-aid-form.component.html',
  styleUrl: './first-aid-form.component.css'
})
export class FirstAidFormComponent implements OnInit {
  currentStep = 1;
  formId: string | null = null;
  loadingSection1 = false;
  loadingSection2 = false;
  form: FormGroup;

  constructor(private fb: FormBuilder, private firstAidService: FirstAidService, private commonFuncs: CommonFunctionsService, private route: ActivatedRoute, private router: Router ) {}

  calculateProgress(): number {
    const completedSections = [
      this.form.value?.employeeReport.reporterCompleted,
      this.form.value?.safetyDirectorReview.safetyDirectorCompleted,
    ].filter(Boolean).length;
    return (completedSections / 2) * 100;
  }

  configureValidatorsForCurrentStep(): void {
    if (this.currentStep === 2) {
      this.form.get('safetyDirectorReview.safetyDirectorSignature')?.setValidators([Validators.required]);
    } 
  }

  loadForm(formId: string): void {
    this.firstAidService.getForm(formId).subscribe(
      (response) => {
        this.form.patchValue({
          employeeReport: {
            userEmail: response['data'].userEmail,
            dateOfReport: response['data'].dateTimeOfFirstAid,
            timeOfReport: response['data'].dateTimeOfFirstAid,
            incidentLocation: response['data'].incidentLocation,
            firstAidResponder: response['data'].firstAidResponder,
            firstAidProvidedTo: response['data'].firstAidProvidedTo,
            typeOfFirstAid: response['data'].typeOfFirstAid,
            whyFirstAidNecessary: response['data'].whyFirstAidNecessary,
            dateReportSubmitted: response['data'].dateReportSubmitted,
            reporterSignature: response['data'].reporterSignature,
            reporterCompleted: response['data'].reporterCompleted
          },
          safetyDirectorReview: {
            safetyDirectorSignature: response['data'].safetyDirectorSignature,
            safetyDirectorCompleted: response['data'].safetyDirectorCompleted,
          }
        });
        this.setCurrentStep();
        this.configureValidatorsForCurrentStep();
        // this.loadEquipmentRequestList(formId); 
      },
      (error) => {
        console.error('Error fetching form data:', error);
      }
    );
  }

  moveToNextStep(): void {
    if (this.currentStep <= 2) {
      this.currentStep++;
      this.configureValidatorsForCurrentStep();
    }
  }

  ngOnInit(): void {
  const today = new Date();
  this.form = this.fb.group({
    employeeReport: this.fb.group({
      userEmail: [null, [Validators.required, Validators.email]],
      dateOfReport: [null,[Validators.required]],
      timeOfReport: [null,[Validators.required]],
      incidentLocation: [null,[Validators.required]],
      firstAidResponder: [null,[Validators.required]],
      firstAidProvidedTo: [null,[Validators.required]],
      typeOfFirstAid: [null,[Validators.maxLength(5000), Validators.required]],
      whyFirstAidNecessary: [null,[Validators.maxLength(5000), Validators.required]],
      dateReportSubmitted: [today,[Validators.required]],
      reporterSignature: [null,[Validators.required]],
      reporterCompleted: [false],
    }),
    safetyDirectorReview: this.fb.group({
      safetyDirectorSignature: [null],
      safetyDirectorCompleted: [false],
    })
  });
  
  this.route.paramMap.subscribe((params) => {
    this.formId = params.get('id'); 
    if (this.formId) {
      this.loadForm(this.formId);
    } else {
      this.setCurrentStep();
      this.configureValidatorsForCurrentStep();
    }
  });
}

   saveSection1(): void {
    this.loadingSection1 = true;

    if (this.form.invalid) {
      this.loadingSection1 = false;
      return;
    }

   const employeeReportValues: FirstAidForm = {
    userEmail: this.form.value.employeeReport.userEmail,
    dateTimeOfFirstAid: this.commonFuncs.convertToUtc(this.commonFuncs.combineDateAndTime(this.form.value.employeeReport.dateOfReport, this.form.value.employeeReport.timeOfReport)) as Date,
    incidentLocation: this.form.value.employeeReport.incidentLocation,
    firstAidResponder: this.form.value.employeeReport.firstAidResponder,
    firstAidProvidedTo: this.form.value.employeeReport.firstAidProvidedTo,
    typeOfFirstAid: this.form.value.employeeReport.typeOfFirstAid,
    whyFirstAidNecessary: this.form.value.employeeReport.whyFirstAidNecessary,
    dateReportSubmitted: this.commonFuncs.convertToUtc(this.form.value.employeeReport.dateReportSubmitted) as Date,
    reporterSignature: this.form.value.employeeReport.reporterSignature,
    reporterCompleted: true,
    safetyDirectorSignature: this.form.value.safetyDirectorReview.safetyDirectorSignature,
    safetyDirectorCompleted: false,
    };
    this.firstAidService.createFirstAid(employeeReportValues).subscribe(
      (response) => {
        this.formId = response.data._id; 
        this.form.get('employeeReport.reporterCompleted')?.setValue(true);
        this.router.navigate(['success']);
        this.loadingSection1 = false;
        this.moveToNextStep();
      },
      (error) => {
        console.error('Error saving employee report:', error);
        this.loadingSection1 = false;
      }
    );
  }

  saveSection2(): void {
    if (this.form.value?.safetyDirectorReview.safetyDirectorCompleted) {
        console.warn('Section 2 is already completed. Cannot re-submit.');
        return;
    }
    this.loadingSection2 = true;
    const safetyDirectorReviewValues: FirstAidForm = {
      userEmail: '',
      dateTimeOfFirstAid: null,
      incidentLocation: '',
      firstAidResponder: '',
      firstAidProvidedTo: '',
      typeOfFirstAid: '',
      whyFirstAidNecessary: '',
      dateReportSubmitted: null,
      reporterSignature: '',
      reporterCompleted: true,
      safetyDirectorSignature: this.form.value.safetyDirectorReview.safetyDirectorSignature,
      safetyDirectorCompleted: true,
    };
    this.firstAidService.updateSection2(this.formId, [safetyDirectorReviewValues]).subscribe(
      (response) => {
        this.form.get('safetyDirectorReview.safetyDirectorCompleted')?.setValue(true);
        this.router.navigate(['success']);
        this.loadingSection2 = false;
        this.moveToNextStep();
      },
      (error) => {
        console.error('Error saving section 2:', error);
        this.loadingSection2 = false;
      }
    );
  }

  setCurrentStep(): void {
    if (this.form.value?.safetyDirectorReview.safetyDirectorCompleted) {
      this.currentStep = 0; 
    } else if (this.form.value?.employeeReport.reporterCompleted) {
      this.currentStep = 2; 
    } else {
      this.currentStep = 1; 
    }
  }
}
